import { OperationOutcomeError } from "@medplum/core";

const VERSION_CONFLICT_STATUS_CODES = new Set([409, 412]);

function hasVersionConflictStatusCode(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  // status only - Medplum errors carry no statusCode/response
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" && VERSION_CONFLICT_STATUS_CODES.has(status);
}

function hasVersionConflictMessageToken(message: string): boolean {
  return (
    message.includes("conflict") || message.includes("precondition") || message.includes("version")
  );
}

/**
 * Detects Medplum optimistic-lock failures for JSON Patch `test` operations and stale versions.
 */
export function isMedplumVersionConflictError(error: unknown): boolean {
  if (error instanceof OperationOutcomeError) {
    const issue = error.outcome?.issue?.[0];
    if (issue?.code === "conflict") {
      return true;
    }

    // Medplum reports a rejected `If-Match` as code "processing" with the text on `details.text`,
    // not on `diagnostics` — and never as a `status`, because OperationOutcomeError carries only
    // `outcome`. Read every field that can hold the reason, then apply the same token list as the
    // plain-Error branch below. Checking only `diagnostics` lets a real 412 fall through and read
    // as an ordinary save failure, which keeps autosave retrying a stale body.
    const outcomeText = [issue?.diagnostics, issue?.details?.text, error.message]
      .filter((text): text is string => !!text?.length)
      .join(" ")
      .toLowerCase();

    if (outcomeText.includes("test failed") || hasVersionConflictMessageToken(outcomeText)) {
      return true;
    }

    return hasVersionConflictStatusCode(error);
  }

  if (hasVersionConflictStatusCode(error)) {
    return true;
  }

  if (error instanceof Error) {
    return hasVersionConflictMessageToken(error.message.toLowerCase());
  }

  return false;
}
